import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  getCurrentUser,
  loginUser,
  refreshUserSession,
  renewUserSession,
  type AuthUser,
  type LoginResponse,
} from '../services/authService';
import { ApiError, setAccessTokenRefreshHandler } from '../services/api';
import { invalidateProjectWorkspaceCache } from '../services/projectWorkspaceCache';

const ACCESS_TOKEN_STORAGE_KEY = 'neuroplan_access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'neuroplan_refresh_token';
const SESSION_REFRESH_LEEWAY_MS = 60_000;
const SESSION_REFRESH_RETRY_MS = 60_000;

type AuthContextValue = {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthProviderProps = {
  children: ReactNode;
  service: {
    set: (state: Record<string, unknown>) => void;
    setUser: (user: AuthUser | null) => void;
    setServiceImplementation: (implementation: Record<string, unknown>) => void;
  };
};

function buildAuthorizationHeader(accessToken: string | null) {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

function getTokenExpirationTime(accessToken: string): number | null {
  try {
    const encodedPayload = accessToken.split('.')[1];
    if (!encodedPayload) {
      return null;
    }

    const normalizedPayload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      '='
    );
    const payload = JSON.parse(window.atob(paddedPayload)) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children, service }: AuthProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const syncServiceState = useCallback(
    (nextUser: AuthUser | null, nextAccessToken: string | null, nextIsLoading: boolean) => {
      service.set({
        enabled: true,
        accessToken: nextAccessToken,
        isLoading: nextIsLoading,
      });
      service.setUser(nextUser);
    },
    [service]
  );

  const clearAuthState = useCallback(
    (clearStoredToken = true) => {
      invalidateProjectWorkspaceCache();
      if (clearStoredToken) {
        localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      }
      setUser(null);
      setAccessToken(null);
      syncServiceState(null, null, false);
    },
    [syncServiceState]
  );

  const logout = useCallback(() => {
    clearAuthState();
    navigate('/', { replace: true });
  }, [clearAuthState, navigate]);

  const applySession = useCallback(
    (session: LoginResponse) => {
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, session.access_token);
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, session.refresh_token);
      setUser(session.user);
      setAccessToken(session.access_token);
      syncServiceState(session.user, session.access_token, false);
    },
    [syncServiceState]
  );

  const refreshSession = useCallback(async (): Promise<string | null> => {
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (!storedRefreshToken) {
      return null;
    }

    try {
      const session = await refreshUserSession(storedRefreshToken);
      applySession(session);
      return session.access_token;
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        clearAuthState();
        return null;
      }
      throw error;
    }
  }, [applySession, clearAuthState]);

  const handleUnauthenticated = useCallback(async () => {
    try {
      const refreshedAccessToken = await refreshSession();
      if (refreshedAccessToken) {
        return refreshedAccessToken;
      }
    } catch {
      // A temporary backend/network problem must not erase a valid renewable session.
      return null;
    }

    const isEmbedded = window.parent !== window;
    clearAuthState(!isEmbedded);
    if (!isEmbedded && location.pathname !== '/') {
      navigate('/', { replace: true });
    }
    return null;
  }, [clearAuthState, location.pathname, navigate, refreshSession]);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await loginUser(email, password);
      applySession(response);
      return response.user;
    },
    [applySession]
  );

  useEffect(
    () => setAccessTokenRefreshHandler(refreshSession),
    [refreshSession]
  );

  useEffect(() => {
    let isCancelled = false;

    const restoreSession = async () => {
      const storedToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

      if (!storedToken && !storedRefreshToken) {
        if (!isCancelled) {
          setIsLoading(false);
          syncServiceState(null, null, false);
        }
        return;
      }

      try {
        if (!storedToken) {
          throw new Error('Access token is missing.');
        }

        const currentUser = await getCurrentUser(storedToken, true);
        if (isCancelled) {
          return;
        }

        if (!storedRefreshToken) {
          try {
            const renewableSession = await renewUserSession(storedToken);
            if (!isCancelled) {
              applySession(renewableSession);
            }
            return;
          } catch {
            // Keep the still-valid legacy session. The next login will provide a refresh token.
          }
        }

        setUser(currentUser);
        setAccessToken(storedToken);
        syncServiceState(currentUser, storedToken, false);
      } catch {
        if (storedRefreshToken) {
          try {
            const session = await refreshUserSession(storedRefreshToken);
            if (!isCancelled) {
              applySession(session);
            }
            return;
          } catch (error) {
            if (
              !isCancelled &&
              error instanceof ApiError &&
              (error.status === 401 || error.status === 403)
            ) {
              clearAuthState(window.parent === window);
            }
            return;
          }
        }

        if (!isCancelled) {
          clearAuthState(window.parent === window);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    restoreSession();

    return () => {
      isCancelled = true;
    };
  }, [applySession, clearAuthState, syncServiceState]);

  useEffect(() => {
    if (!accessToken || !localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)) {
      return;
    }

    let timerId: number | undefined;
    let isCancelled = false;
    const expirationTime = getTokenExpirationTime(accessToken);
    const initialDelay = expirationTime
      ? Math.max(expirationTime - Date.now() - SESSION_REFRESH_LEEWAY_MS, 0)
      : SESSION_REFRESH_RETRY_MS;

    const renewSession = async () => {
      let refreshedToken: string | null = null;
      try {
        refreshedToken = await refreshSession();
      } catch {
        // Keep the session and retry after a short delay when the backend is reachable again.
      }
      if (
        !isCancelled &&
        !refreshedToken &&
        localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)
      ) {
        timerId = window.setTimeout(renewSession, SESSION_REFRESH_RETRY_MS);
      }
    };

    timerId = window.setTimeout(renewSession, initialDelay);

    return () => {
      isCancelled = true;
      if (timerId !== undefined) {
        window.clearTimeout(timerId);
      }
    };
  }, [accessToken, refreshSession]);

  useEffect(() => {
    service.setServiceImplementation({
      getAuthorizationHeader: () => buildAuthorizationHeader(accessToken),
      handleUnauthenticated,
    });
  }, [accessToken, handleUnauthenticated, service]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      accessToken,
      isAuthenticated: Boolean(user && accessToken),
      isLoading,
      login,
      logout,
    }),
    [accessToken, isLoading, login, logout, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }

  return context;
}
